// A scan-a-box field for screens that take box after box (the Section card, the Pick list).
// Typed or scanned, it submits on Enter. A browser keeps it focused; the kiosk's handhelds
// deliver scans by HID to the page listener (useScannerInput), so no field is focused there.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fieldBase } from '../components/review/fieldStyles';
import { FOCUS, MOTION } from '../Requisitions/buttonStyles';
import useScannerInput from './hooks/useScannerInput';
import { ScanFrameIcon } from './icons';
import { focusForScan } from './ui';
import { inShell } from './shell';

// Module scope so the hook's listeners keep a stable identity.
const isBoxCode = (val: string) => /^\S{4,}$/.test(val);

interface Props {
  id: string;
  label: string;
  placeholder: string;
  busy: boolean;
  onScan: (code: string) => void;
}

const BoxScan: React.FC<Props> = ({ id, label, placeholder, busy, onScan }) => {
  const ref = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');

  const send = useCallback((raw: string) => {
    const code = raw.trim();
    if (code) onScan(code);
  }, [onScan]);

  useScannerInput({ onScan: send, surface: 'lookup', minLength: 4, validator: isBoxCode, disabled: busy });

  // Back to the field once an action settles, ready for the next box.
  useEffect(() => {
    if (!busy) focusForScan(ref);
  }, [busy]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const code = value.trim();
    if (!code || busy) return;
    setValue('');
    send(code);
    if (inShell()) ref.current?.blur();
  };

  return (
    <form onSubmit={submit}>
      <label htmlFor={id} className="mb-1 block text-sm text-on-surface-variant">{label}</label>
      <div className={`flex items-center gap-1 rounded-full py-1.5 pl-4 pr-1.5 ring-1 ring-primary/30 ${fieldBase} focus-within:ring-2 focus-within:ring-primary/60`}>
        <ScanFrameIcon className="h-7 w-7 shrink-0 text-on-surface-variant" />
        <input
          id={id}
          ref={ref}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="go"
          className="m-0 min-h-[2.75rem] min-w-0 flex-1 bg-transparent px-2 font-mono text-lg text-on-surface placeholder:font-sans placeholder:text-base placeholder:text-on-surface-variant focus:outline-none"
        />
        <button
          type="submit"
          disabled={!value.trim() || busy}
          className={`inline-flex h-11 shrink-0 items-center whitespace-nowrap rounded-full bg-primary px-5 text-sm font-semibold text-on-primary np-state np-state-on-primary disabled:opacity-40 ${FOCUS} ${MOTION}`}
        >
          {busy ? 'Working…' : 'Go'}
        </button>
      </div>
    </form>
  );
};

export default BoxScan;
